// Shared renderer for the locally vendored Material Symbols artwork.
import type { ComponentType, SVGProps } from "react";

export type MaterialIconProps = SVGProps<SVGSVGElement> & {
  size?: number | string;
  opticalSize?: 20 | 24;
  filled?: boolean;
  mirrored?: boolean;
  title?: string;
};

export type MaterialIcon = ComponentType<MaterialIconProps>;

type Drawing = {
  viewBox: string;
  paths: readonly string[];
};

type Artwork = {
  outline20: Drawing;
  outline24: Drawing;
  filled20: Drawing;
  filled24: Drawing;
};

export function MaterialSymbol({
  artwork,
  symbol,
  size = 24,
  opticalSize = typeof size === "number" && size > 20 ? 24 : 20,
  filled = false,
  mirrored = false,
  title,
  children,
  style,
  "aria-label": ariaLabel,
  "aria-labelledby": ariaLabelledBy,
  "aria-hidden": ariaHidden,
  role,
  ...props
}: MaterialIconProps & { artwork: Artwork; symbol: string }) {
  const named = Boolean(title || ariaLabel || ariaLabelledBy);
  const drawing = artwork[`${filled ? "filled" : "outline"}${opticalSize}`];
  return (
    <svg
      width={size}
      height={size}
      viewBox={drawing.viewBox}
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
      focusable="false"
      aria-label={ariaLabel ?? title}
      aria-labelledby={ariaLabelledBy}
      aria-hidden={ariaHidden ?? (named ? undefined : true)}
      role={role ?? (named ? "img" : undefined)}
      data-material-symbol={symbol}
      data-optical-size={opticalSize}
      data-filled={filled ? "true" : "false"}
      style={mirrored ? { ...style, transform: `scaleX(-1) ${style?.transform ?? ""}`.trim() } : style}
      {...props}
    >
      {title ? <title>{title}</title> : null}
      {drawing.paths.map((path, index) => <path key={index} d={path} />)}
      {children}
    </svg>
  );
}
